/* ============================================================
   PENS PERSONALISED — MENU DRAWER PORTAL
   File: assets/pp-menu-portal.js

   WHY THIS EXISTS

   The menu drawer lives inside #header-group. Something in that
   subtree creates a stacking context and a containing block — a
   transform, filter or backdrop-filter on an ancestor. Two
   consequences, both of which we saw:

     1. The drawer's z-index only ranked it INSIDE that context,
        so page sections painted over it however high we set it.
     2. `position: fixed` resolved against that ancestor rather
        than the viewport, so the panel was cut short.

   No CSS from outside can undo a containing block, so while the
   menu is open the panel and backdrop are moved to <body>.

   SIDE EFFECT, HANDLED BELOW

   Horizon binds the drawer's buttons declaratively — markup
   like on:click="/close" is handled by a listener on the
   <header-drawer> element itself. Once the panel is moved out
   of that element those clicks are no longer heard, which is
   why the close button stopped working. The controls are
   therefore wired directly here while the panel is away.

   26 Sep 2026: the country/currency selector inside the drawer
   (details.drawer-localization) is a submenu too, so it is now
   handled by the same open/close wiring as the menu items.
   ============================================================ */

(function () {
  var details = document.querySelector('details.menu-drawer-container');
  if (!details) return;

  var component = document.querySelector('header-drawer');
  var panel = details.querySelector('.menu-drawer');
  var backdrop = details.querySelector('.menu-drawer__backdrop');
  if (!panel) return;

  var panelHome = document.createComment('pp-menu-panel');
  var backdropHome = document.createComment('pp-menu-backdrop');
  var ported = false;

  /* ── Closing ──
     Prefer the theme's own method so its focus handling and
     scroll lock unwind properly. Fall back to the DOM state it
     sets, if the method is unavailable. */
  function closeMenu() {
    if (component && typeof component.close === 'function') {
      component.close();
      return;
    }
    details.classList.remove('menu-open');
    details.removeAttribute('open');
    var summary = details.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'false');
    panel.classList.remove('menu-drawer--has-submenu-opened');
  }

  /* ── Going back from a submenu ── */
  function backOne(target) {
    var open = target.closest('details[open]');
    if (open && open !== details) {
      open.classList.remove('menu-open');
      open.removeAttribute('open');
      panel.classList.remove('menu-drawer--has-submenu-opened');
      return;
    }
    closeMenu();
  }

  function onPanelClick(event) {
    var close = event.target.closest('.menu-drawer__close-button');
    if (close) {
      event.preventDefault();
      event.stopPropagation();
      closeMenu();
      return;
    }

    var back = event.target.closest('.menu-drawer__back-button');
    if (back) {
      event.preventDefault();
      event.stopPropagation();
      backOne(back);
      return;
    }

    /* Opening a submenu (menu item or the country/currency
       selector): the theme adds these classes itself, but its
       listener cannot hear us while we are portaled */
    var openSub = event.target.closest(
      'summary.menu-drawer__menu-item--mainlist, details.drawer-localization > summary'
    );
    if (openSub) {
      var parent = openSub.parentElement;
      if (parent && parent.tagName === 'DETAILS') {
        setTimeout(function () {
          if (parent.hasAttribute('open')) {
            parent.classList.add('menu-open');
            panel.classList.add('menu-drawer--has-submenu-opened');
          } else {
            parent.classList.remove('menu-open');
            panel.classList.remove('menu-drawer--has-submenu-opened');
          }
        }, 0);
      }
    }
  }

  function onBackdropClick(event) {
    event.preventDefault();
    closeMenu();
  }

  function onKey(event) {
    if (event.key === 'Escape' && ported) closeMenu();
  }

  function portal() {
    if (ported) return;

    panel.parentNode.insertBefore(panelHome, panel);
    document.body.appendChild(panel);
    panel.classList.add('pp-portaled');

    if (backdrop) {
      backdrop.parentNode.insertBefore(backdropHome, backdrop);
      document.body.appendChild(backdrop);
      backdrop.classList.add('pp-portaled-backdrop');
      backdrop.addEventListener('click', onBackdropClick);
    }

    panel.addEventListener('click', onPanelClick, true);
    document.addEventListener('keydown', onKey);

    ported = true;
  }

  function restore() {
    if (!ported) return;

    panel.removeEventListener('click', onPanelClick, true);
    document.removeEventListener('keydown', onKey);
    if (backdrop) backdrop.removeEventListener('click', onBackdropClick);

    panel.classList.remove('pp-portaled');
    if (panelHome.parentNode) {
      panelHome.parentNode.insertBefore(panel, panelHome);
      panelHome.remove();
    }

    if (backdrop) {
      backdrop.classList.remove('pp-portaled-backdrop');
      if (backdropHome.parentNode) {
        backdropHome.parentNode.insertBefore(backdrop, backdropHome);
        backdropHome.remove();
      }
    }

    ported = false;
  }

  function sync() {
    var open = details.hasAttribute('open') || details.classList.contains('menu-open');
    if (open) {
      portal();
    } else {
      setTimeout(restore, 260);
    }
  }

  new MutationObserver(sync).observe(details, {
    attributes: true,
    attributeFilter: ['open', 'class'],
  });

  sync();
})();
